import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ROLE_CARDS = [
  {
    id: 'investor',
    label: 'Investor',
    icon: 'User',
    description: 'View portfolio, opportunities, and documents. Limited write access.',
    color: 'text-blue-700',
    bg: 'bg-[rgba(59,76,76,0.07)]',
    border: 'border-blue-200',
  },
  {
    id: 'sponsor',
    label: 'Sponsor',
    icon: 'Building2',
    description: 'Manage deals, upload documents, and communicate with investors.',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: 'ShieldCheck',
    description: 'Full platform access including settings, team management, and billing.',
    color: 'text-[#3b4c4c]',
    bg: 'bg-[#3b4c4c]/5',
    border: 'border-[#3b4c4c]/20',
  },
];

export default function RolesSection({ showToast, userRole, onRoleChange }) {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState(userRole || 'admin');

  const handleSwitch = (roleId) => {
    setActiveRole(roleId);
    if (onRoleChange) onRoleChange(roleId);
    showToast(`Role switched to ${roleId?.charAt(0)?.toUpperCase() + roleId?.slice(1)}`);
  };

  return (
    <div>
      {/* Link to Team Management */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#3b4c4c]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Users" size={20} color="#3b4c4c" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900 mb-1">Role Definitions & Permission Matrix</h3>
            <p className="text-sm text-slate-500 mb-4">Manage role definitions and the full permission matrix from the Team Management screen. Configure which actions each role can perform across the platform.</p>
            <button
              onClick={() => navigate('/team-management')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] transition-colors shadow-sm"
            >
              <Icon name="ExternalLink" size={15} />
              Go to Team Management
            </button>
          </div>
        </div>
      </div>
      {/* Demo Role Switcher */}
      <div className="bg-white rounded-2xl border-2 border-[#3b4c4c]/20 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <Icon name="Zap" size={16} color="#d97706" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Demo Mode — Role Switcher</h3>
          </div>
          <span className="ml-auto px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Demo Only</span>
        </div>
        <p className="text-sm text-slate-500 mb-6">Switch your active role to explore the platform from different perspectives. Changes take effect immediately.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ROLE_CARDS?.map(role => {
            const isActive = activeRole === role?.id;
            return (
              <div
                key={role?.id}
                className={`relative rounded-2xl border-2 p-5 transition-all ${
                  isActive
                    ? 'border-[#3b4c4c] bg-[#3b4c4c]/5 shadow-md'
                    : `${role?.border} bg-white hover:shadow-sm`
                }`}
              >
                {isActive && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-bold bg-[#3b4c4c] text-white">Active</span>
                )}
                <div className={`w-10 h-10 rounded-xl ${role?.bg} flex items-center justify-center mb-3`}>
                  <Icon name={role?.icon} size={20} color={isActive ? '#3b4c4c' : undefined} />
                </div>
                <h4 className="text-sm font-bold text-slate-900 mb-1">{role?.label}</h4>
                <p className="text-xs text-slate-500 mb-4 leading-relaxed">{role?.description}</p>
                <button
                  onClick={() => !isActive && handleSwitch(role?.id)}
                  disabled={isActive}
                  className={`w-full py-2 rounded-xl text-xs font-semibold transition-colors ${
                    isActive
                      ? 'bg-[#3b4c4c] text-white cursor-default'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isActive ? 'Current Role' : `Switch to ${role?.label}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5">
          <Icon name="Info" size={12} />
          This feature is only available in Demo Mode.
        </p>
      </div>
    </div>
  );
}
